import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import ProgressBar from "../../components/ProgressBar";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";

import Link from "@mui/material/Link";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import Select from "react-select";

import { NumericFormat } from "react-number-format";
import { TextField } from "@mui/material";

import { isMobile } from "react-device-detect";
import isEmpty from "lodash-es/isEmpty";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import { doService } from "../../services/ServiceHelper";
import {
  createPassByTeacher,
  getPassTypes,
  getPassLocations,
} from "../../services/PassService";
import { getPassesByStudent } from "../../services/ReportService";
import { getSchoolStudents, isAdmin } from "../../services/UserService";
import { getSchoolTeachers } from "../../services/SchoolService";
import { getToday } from "../../services/DateService";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import {
  StripedTableRow,
  StripedTableCell,
} from "../../components/StripedTable";
import TableRowNoResults from "../TableRowNoResults";

function CreatePassDialog({ initialStudents = [], onClose, onOK, open, defaultBathroomLocationId=null }) {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_students, setStudents] = React.useState([]);
  const [_teachers, setTeachers] = React.useState([]);
  const [_locations, setLocations] = React.useState([]);
  const [_locationsRaw, setLocationsRaw] = React.useState([]);

  const [_rawPassTypes, setRawPassTypes] = React.useState([]);
  const [_passTypes, setPassTypes] = React.useState([]);

  const [_selectedStudents, setSelectedStudents] = React.useState([]);
  const [, setSelectedFromTeacher] = React.useState("");
  const [_selectedToTeacher, setSelectedToTeacher] = React.useState("");
  const [_selectedToLocation, setSelectedToLocation] = React.useState("");
  const [_selectedToOption, setSelectedToOption] = React.useState("");
  const [_selectedPassType, setSelectedPassType] = React.useState("");

  const [_timeoutMinutes, setTimeoutMinutes] = React.useState(15);
  const [_requiresLocation, setRequiresLocation] = React.useState(true);

  const [_maxWidth, setMaxWidth] = React.useState(400);
  const [_dateStart, setDateStart] = useState(dayjs());
  const [_timeStart, setTimeStart] = useState(dayjs());
  const [_reason, setReason] = useState("");
  const [_teacherComment, setTeacherComment] = useState("");

  const isPassEnabled = () => _school != null && _school.passEnabled === "Y";
  const [_showProgress, setShowProgress] = useState(false);
  const [_showHistoryProgress, setShowHistoryProgress] = useState(false);
  const [_selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const [_toggleAdvancedSettings, setToggleAdvancedSettings] = useState(false);
  const [_historyResults, setHistoryResults] = useState([]);

  const MIN_TIMEOUT_VALUE = 1;
  const MAX_TIMEOUT_VALUE = 30;

  const TO_OPTIONS = [
    { value: "L", label: "LOCATION" },
    { value: "T", label: "TEACHER" },
  ];

  useEffect(() => {
  }, []);

  useEffect(() => {
    if (open !== true || !isPassEnabled()) {
      return;
    }

    if (_passTypes.length === 0) {
      initUi();
      initStudents();
      initTeachers();
      initPassLocations();
      initPassTypes();
    }
  }, [open, _passTypes.length]);

  const initUi = () => {
    if (isMobile) {
      setMaxWidth(450);
    } else {
      setMaxWidth(750);
    }
  };

  const initPassLocations = () => {
    doService(getPassLocations(_school, _jwt), (result) => {
      const labeled = [];
      for (const location of result.data) {
        labeled.push({
          label: location.name,
          value: location.id,
        });
      }
      setLocations(labeled);
      setLocationsRaw(result.data);
    });
  };

  const initTeachers = () => {
    doService(getSchoolTeachers(_school, _jwt), (result) => {
      const labeledTeachers = [];
      for (const teacher of result.data) {
        labeledTeachers.push({
          label: `${teacher.last_name}, ${teacher.first_name}`,
          value: teacher.eid,
        });
      }
      setTeachers(labeledTeachers);
    });
  };

  const initStudents = () => {
    doService(getSchoolStudents(_school, _jwt), (results) => {
      const labeledStudents = [];
      const initialStudentsMap = {};
      const selectedStudents = [];

      // store the eid as a key and value
      for (const initialStudent of initialStudents) {
        initialStudentsMap[initialStudent] = initialStudent;
      }

      for (const student of results.data) {
        const entry = {
          label: `${student.last_name}, ${student.first_name} (${student.grade})`,
          value: student.eid,
        };
        labeledStudents.push(entry);

        // if this student is an initial student then pre-populate
        if (initialStudentsMap[student.eid] != null) {
          selectedStudents.push(entry);
        }
      }
      setStudents(labeledStudents);
      setSelectedStudents(selectedStudents);
    });
  };

  const initPassTypes = () => {
    doService(getPassTypes(_school, _jwt), (result) => {
      const types = [];
      for (const type of result.data) {
        types.push({
          label: type.name,
          value: type.eid,
        });
      }
      setRawPassTypes(result.data);
      setPassTypes(types);
    });
  };

  const handleClose = () => {
    onClose();
  };

  const isValid = (values) => {
    let valid = false;

    const passType = getPassType(values.type_eid);

    if (values.student_eids.length === 0) {
      alert("Please select 1 or more students.");
    } else if (isEmpty(values.to_option) && passType.requiresLocation === "Y") {
      alert("Please select a destination Location or Teacher.");
    } else if (isEmpty(values.type_eid)) {
      alert("Please select a Pass Type.");
    } else if (
      values.to_option === "L" &&
      isEmpty("" + values.to_location_id)
    ) {
      alert("Please select a destination Location.");
    } else if (values.to_option === "T" && isEmpty(values.to_teacher_eid)) {
      alert("Please select a destination Teacher.");
    } else if (isEmpty(values.date)) {
      alert("Please select a date.");
    } else if (isEmpty(values.time)) {
      alert("Please select a time.");
    } else if (
      values.timeout < MIN_TIMEOUT_VALUE ||
      values.timeout > MAX_TIMEOUT_VALUE
    ) {
      alert("Please enter a timeout between 1 and 30 minutes.");
    } else {
      valid = true;
    }

    return valid;
  };

  let timeStart = _timeStart;
  if (typeof timeStart === "string") {
    timeStart = dayjs(timeStart, "HH:mma");
    setTimeStart(timeStart);
  }

  const getValues = () => {
    const values = {
      student_eids: [],
      type_eid: _selectedPassType,
      to_option: _selectedToOption.value,
      reason: _reason,
      timeout: _timeoutMinutes,
      to_teacher_eid: _selectedToTeacher,
      to_location_id: _selectedToLocation.value,
      date: _dateStart.format("YYYY-MM-DD"),
      time: timeStart.format("hh:mmA"),
      teacher_comment: _teacherComment,
    };

    for (let selectedStudent of _selectedStudents) {
      values.student_eids.push(selectedStudent.value);
    }

    return values;
  };

  const onTabChange = (event, index) => {
    setSelectedTabIndex(index);
  };

  const handleOK = () => {
    const values = getValues();
    if (!isValid(values)) {
      return;
    }

    setShowProgress(true);
    doService(
      createPassByTeacher(_school, _jwt, values),
      (result) => {
        onOK(_selectedStudents);
        setSelectedStudents([]);
        setDateStart(dayjs());
        setTimeStart(dayjs());
        setReason("");
        setTeacherComment("");
        setShowProgress(false);
        window.showSnack("Pass Created");
      },
      (error) => {
        setShowProgress(false);
        alert("Unable to Create Pass: " + error);
      },
      () => {
        setShowProgress(false);
      },
    );
  };

  const handleStudentSelect = (studentEids) => {
    setSelectedStudents(studentEids);

    if (studentEids.length === 0) {
      setHistoryResults([]);
      return;
    }

    const dateEnd = getToday();
    const dateStart = dayjs().subtract(3, "day").format("YYYY-MM-DD");

    setShowHistoryProgress(true);
    doService(
      getPassesByStudent(
        _school,
        _jwt,
        studentEids[0].value,
        dateStart,
        dateEnd,
        "A",
        "",
        "",
      ),
      (results) => {
        setHistoryResults(results.data);
      },
    ).finally(() => {
      setShowHistoryProgress(false);
    });
  };

  const getPassType = (eid) => {
    for (let p of _rawPassTypes) {
      if (p.eid === eid) {
        return p;
      }
    }
    return null;
  };

  const handleChangeTimeoutMinutes = (event) =>
    setTimeoutMinutes(event.target.value);
  const handleChangeFromTeacher = (obj) => setSelectedFromTeacher(obj.value);
  const handleChangeToTeacher = (obj) => setSelectedToTeacher(obj.value);
  const handleChangeToLocation = (obj) => setSelectedToLocation(obj);
  const handleChangeToOption = (obj) => setSelectedToOption(obj);

  const handleChangePassType = (obj) => {
    setSelectedPassType(obj.value);
    const passType = getPassType(obj.value);

    if (passType == null) {
      return;
    }

    setTimeoutMinutes(passType.timeout);
    setRequiresLocation(passType.requiresLocation === "Y");
    console.log("passType", passType);

    if (passType.defaultLocationEid != null) {
      setSelectedToOption({ label: "LOCATION", value: "L" });
      setSelectedToLocation(getLocationByEid(passType.defaultLocationEid));
    }

    if (passType.isBathroom === "Y") {
      setSelectedToOption({ label: "LOCATION", value: "L" });
      // see if the teacher has a bathroom set for them
      // see if there is a default bathroom location ID
      if (defaultBathroomLocationId) {
        setSelectedToLocation(getLocationById(defaultBathroomLocationId));
      }
    }

  };

  const handleChangeReason = (event) => setReason(event.target.value);
  const handleChangeTeacherComment = (event) =>
    setTeacherComment(event.target.value);

  const getLocationByEid = (eid) => {
    for (let location of _locationsRaw) {
      if (location.eid === eid) {
        console.log("FOUND LOCATION", location);
        return { label: location.name, value: location.id };
      }
    }
    return null;
  };

  const getLocationById = (id) => {
    for (let location of _locationsRaw) {
      if (location.id === id) {
        console.log("FOUND LOCATION", location);
        return { label: location.name, value: location.id };
      }
    }
    return null;
  };

  const onToggleAdvancedSettings = () => {
    setToggleAdvancedSettings(!_toggleAdvancedSettings);
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth={true}
      maxWidth={"lg"}
      PaperProps={{
        sx: {
          maxHeight: 550,
          height: 550,
        },
      }}
    >
      <DialogTitle id="select-student-list-dialog-title">
        Manage Pass
      </DialogTitle>

      <DialogContent>
        <ProgressBar showProgress={_showProgress} />
        <InputLabel>1. Select Student(s) *</InputLabel>
        <Select
          options={_students}
          onChange={handleStudentSelect}
          isMulti={true}
          value={_selectedStudents}
        />

        <br />

        <Tabs value={_selectedTabIndex} onChange={onTabChange}>
          <Tab
            label="Create Pass"
            sx={{
              borderRight: "1px solid #ccc",
              minHeight: 48,
            }}
          ></Tab>
          <Tab
            label={`Past Passes (${_historyResults.length})`}
            sx={{
              borderRight: "1px solid #ccc",
              minHeight: 48,
            }}
          ></Tab>
        </Tabs>
        <Container
          sx={{
            ml: 0,
            pl: 0,
            mr: 0,
            pr: 0,
            border: "1px solid #e8e8e8",
            width: 900,
            maxWidth: "100%",
          }}
        >
          {_selectedTabIndex === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <InputLabel sx={{ mt: 1 }}>2. Pass Type *</InputLabel>
                <Select
                  options={_passTypes}
                  onChange={handleChangePassType}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Grid>
              {isAdmin() && (
                <Grid item xs={4}>
                  <InputLabel sx={{ mt: 1 }}>From Teacher *</InputLabel>
                  <Select
                    options={_teachers}
                    onChange={handleChangeFromTeacher}
                  />
                </Grid>
              )}
              {!isEmpty(_selectedPassType) && _requiresLocation === true && (
                <>
                  <Grid item xs={4}>
                    <InputLabel sx={{ mt: 1 }}>3. Go To *</InputLabel>
                    <Select
                      options={TO_OPTIONS}
                      onChange={handleChangeToOption}
                      value={_selectedToOption}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {_selectedToOption.value === "L" && (
                      <>
                        <InputLabel sx={{ mt: 1 }}>4. To Location *</InputLabel>
                        <Select
                          options={_locations}
                          onChange={handleChangeToLocation}
                          value={_selectedToLocation}
                        />
                      </>
                    )}
                    {_selectedToOption.value === "T" && (
                      <>
                        <InputLabel sx={{ mt: 1 }}>4. To Teacher *</InputLabel>
                        <Select
                          options={_teachers}
                          onChange={handleChangeToTeacher}
                        />
                      </>
                    )}
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Link component="button" onClick={onToggleAdvancedSettings}>
                  Advanced Settings &gt;&gt;
                </Link>

                {_toggleAdvancedSettings === true && (
                  <Grid container spacing={1} sx={{ mt: 0.0 }}>
                    <Grid item xs={3}>
                      <InputLabel>Pass Date *</InputLabel>
                      <MobileDatePicker
                        slotProps={{ textField: { size: "small" } }}
                        value={_dateStart}
                        onChange={(newValue) => {
                          setDateStart(newValue);
                        }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <InputLabel>Start Time *</InputLabel>
                      <MobileTimePicker
                        slotProps={{ textField: { size: "small" } }}
                        defaultValue={dayjs()}
                        onChange={(djValue) => {
                          setTimeStart(djValue.format("hh:mmA"));
                        }}
                      />
                    </Grid>
                    {!isEmpty(_selectedPassType) && (
                      <Grid item xs={2}>
                        <InputLabel>Timeout *</InputLabel>
                        <NumericFormat
                          onChange={handleChangeTimeoutMinutes}
                          value={_timeoutMinutes}
                          style={{
                            height: "32px",
                            borderColor: "#c6c6c6",
                            borderStyle: "solid",
                            borderWidth: 1,
                            width: "32px",
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <InputLabel>Teacher Comment (OPTIONAL)</InputLabel>
                      <TextField
                        margin="dense"
                        type="text"
                        size="small"
                        fullWidth
                        variant="outlined"
                        onChange={handleChangeTeacherComment}
                        placeholder="Optional"
                        value={_teacherComment}
                      />
                    </Grid>
                  </Grid>
                )}

                <br />
                <br />
              </Grid>
            </Grid>
          )}

          {_selectedTabIndex === 1 && (
            <>
              {_showHistoryProgress === true && (
                <CircularProgress color="inherit" sx={{ mt: 2, mb: 2 }} />
              )}
              {_showHistoryProgress === false && (
                <>
                  <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center" width={100}>Created</StripedTableCell>
                          <StripedTableCell>Student</StripedTableCell>
                          <StripedTableCell>Type</StripedTableCell>
                          <StripedTableCell align="center">
                            Status
                          </StripedTableCell>
                          <StripedTableCell>From/To</StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRowNoResults data={_historyResults} />
                        {_historyResults.map((entry) => (
                          <StripedTableRow>
                            <StripedTableCell align="center">{dayjs(entry.created_at).format("MM/DD hh:mm")}</StripedTableCell>
                            <StripedTableCell>{entry.student_last_name}, {entry.student_first_name}</StripedTableCell>
                            <StripedTableCell>{entry.type_name}</StripedTableCell>
                            <StripedTableCell align="center">{entry.statusText}</StripedTableCell>
                            <StripedTableCell>FROM: {entry.from_teacher_last_name}, {entry.from_teacher_first_name.substring(0,1)}.<br/>
                             TO: {entry.to_location_name}</StripedTableCell>
                          </StripedTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </Container>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleOK}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreatePassDialog;
